import { ChartConfiguration } from 'chart.js';
import { DashboardPeriodEcho, DashboardWireScope } from './dashboard-filters.model';
import { DashboardLink } from './dashboard-link.model';
import { DashboardVm } from './dashboard-vm.model';
import { PipelineRatio } from './pipeline-health.model';

/** The seven cards, in the ticket's order; each value is the card's wire key in `best` and `links`. */
export enum ScorecardChart {
  FUNDED_AMOUNT = 'fundedAmount',
  FUNDED_UNITS = 'fundedUnits',
  SUBMITTED = 'submitted',
  APPROVED = 'approved',
  APPROVAL_RATE = 'approvalRate',
  BOOKING_RATE = 'bookingRate',
  DECLINED_REASONS = 'declinedReasons',
}

/** The six month series; each value is also the month field it reads. */
export type ScorecardSeries = Exclude<ScorecardChart, ScorecardChart.DECLINED_REASONS>;

export const SCORECARD_SERIES: readonly ScorecardSeries[] = [
  ScorecardChart.FUNDED_AMOUNT,
  ScorecardChart.FUNDED_UNITS,
  ScorecardChart.SUBMITTED,
  ScorecardChart.APPROVED,
  ScorecardChart.APPROVAL_RATE,
  ScorecardChart.BOOKING_RATE,
];

export interface ScorecardMonth {
  /** `YYYY-MM`, an Eastern month; the last one is month to date. */
  month: string;
  fundedAmount: number;
  fundedUnits: number;
  submitted: number;
  approved: number;
  approvalRate: PipelineRatio;
  bookingRate: PipelineRatio;
}

export interface ScorecardDeclinedReason {
  reason: string;
  count: number;
  /** Competition rank: tied counts share one, and every rank 1 is top. */
  rank: number;
}

export interface ScorecardDeclinedReasons {
  /** At most 15, count descending. */
  reasons: ScorecardDeclinedReason[];
  /** Reasons cut by the cap. */
  remainingReasons: number;
  /** Declines the cut reasons hold. */
  remainingCount: number;
}

export interface PartnerScorecard {
  asOfUtc: Date;
  /** Eastern, half-open. */
  window: DashboardPeriodEcho;
  /** Oldest first. */
  months: ScorecardMonth[];
  /** The index into `months` of the bar to paint, decided by the server; null paints none. */
  best: Record<ScorecardSeries, number | null>;
  declinedReasons: ScorecardDeclinedReasons;
  /** Null renders no VIEW REPORT button. */
  links: Record<ScorecardChart, DashboardLink | null>;
}

/** Scope is the endpoint's only parameter. */
export interface PartnerScorecardRequest {
  scope: DashboardWireScope;
}

export interface ScorecardChartVm {
  chart: ScorecardChart;
  title: string;
  subtitle: string;
  data: ChartConfiguration<'bar'>['data'];
  options: ChartConfiguration<'bar'>['options'];
  ariaLabel: string;
  /** Every value is 0 or null. */
  isEmpty: boolean;
  /** Shown in place of the canvas when `isEmpty`. */
  emptyMessage: string;
  footerLabel: string;
  /** Null renders no VIEW REPORT button. */
  link: DashboardLink | null;
}

export interface ScorecardDeclinedChartVm extends ScorecardChartVm {
  /** `+N more`; null when nothing was cut. */
  moreLabel: string | null;
}

export interface PartnerScorecardDataVm {
  /** The six month cards, in `SCORECARD_SERIES` order. */
  charts: ScorecardChartVm[];
  declined: ScorecardDeclinedChartVm;
}

export type PartnerScorecardVm = DashboardVm<PartnerScorecardDataVm>;
